/**
 * Validations API Module
 *
 * This module provides functions for interacting with validation endpoints
 * of the WealthBee Enterprise API.
 */
import axios from "axios";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";

/**
 * Interface for validation rule according to OpenAPI spec
 */
export interface ValidationRule {
  fundKey: string;
  ruleKey: string;
  payload: string;
}

/**
 * Interface for key-value pair returned by validation rules endpoints
 */
export interface StringStringKeyValuePair {
  key: string | null;
  value: string | null;
}

/**
 * Interface for validation rules used in the UI
 * Extends StringStringKeyValuePair with additional frontend-specific properties
 */
export interface FundValidationRule extends StringStringKeyValuePair {
  enabled: boolean;
}

/**
 * Interface for validation response (legacy - keeping for backward compatibility)
 */
export interface Validation {
  id: number;
  reportingPeriodId: number;
  entityType: string;
  entityId: number;
  ruleId: number;
  ruleName: string;
  status: "passed" | "failed" | "warning";
  message: string;
  severity: "low" | "medium" | "high" | "critical";
  details?: Record<string, any>;
  timestamp: string;
}

/**
 * Interface for ValidationRuleDto according to OpenAPI spec
 */
export interface ValidationRuleDto {
  validationRuleId?: number | null;
  clientId: number;
  fundId?: number | null;
  ruleName?: string | null;
  ruleType?: string | null;
  accounts?: string | null;
  excludeAccounts?: string | null;
  exceptionRule?: string | null;
  scope?: string | null;
  shortDescriptionTemplate?: string | null;
  descriptionTemplate?: string | null;
  disabled: boolean;
}



/**
 * Retrieves all available validation rules
 *
 * @returns Promise resolving to an array of StringStringKeyValuePair objects
 */
export const getValidationRules = async (): Promise<
  StringStringKeyValuePair[]
> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/Validations/rules`,
      buildClientHeader()
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve validation rules", response.statusText);
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error("Error getting validation rules:", error);
    return [];
  }
};

/**
 * Retrieves validation rules for a specific fund
 *
 * @param fundId - The ID of the fund to get validation rules for
 * @returns Promise resolving to an array of StringStringKeyValuePair objects
 */
export const getValidationRulesForFund = async (
  fundId: number
): Promise<StringStringKeyValuePair[]> => {
  try {
    const baseHeaders = buildClientHeader();
    // Add fundId to headers as specified in the OpenAPI spec
    const headers = {
      ...baseHeaders,
      headers: {
        ...baseHeaders.headers,
        fundId: fundId.toString(),
      },
    };

    const response = await axios.get(
      `${getServiceEndpoint()}/Validations/rules`,
      headers
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log(
        "Failed to retrieve validation rules for fund",
        response.statusText
      );
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error("Error getting validation rules for fund:", error);
    return [];
  }
};

/**
 * Creates or updates a validation rule
 *
 * @param validationRule - The validation rule to create/update
 * @returns Promise resolving to an array of StringStringKeyValuePair objects
 */
export const createValidationRule = async (
  validationRule: ValidationRule
): Promise<StringStringKeyValuePair[]> => {
  try {
    // Ensure fundKey follows the required format: "validation-rules.{fundKey}"
    const formattedValidationRule = {
      ...validationRule,
      fundKey: `${validationRule.fundKey}`,
    };

    const response = await axios.post(
      `${getServiceEndpoint()}/Validations/rules`,
      formattedValidationRule,
      buildClientHeader()
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to create validation rule", response.statusText);
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error("Error creating validation rule:", error);
    return [];
  }
};

/**
 * Retrieves validation rules using the new ValidationRules endpoint
 *
 * @param fundId - Optional fund ID to filter validation rules
 * @returns Promise resolving to an array of ValidationRuleDto objects
 */
export const getValidationRulesNew = async (
  fundId?: number
): Promise<ValidationRuleDto[]> => {
  try {
    const baseHeaders = buildClientHeader();
    const headers = fundId
      ? {
          ...baseHeaders,
          headers: {
            ...baseHeaders.headers,
            fundId: fundId.toString(),
          },
        }
      : baseHeaders;

    const response = await axios.get(
      `${getServiceEndpoint()}/ValidationRules`,
      headers
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve validation rules", response.statusText);
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error("Error getting validation rules:", error);
    return [];
  }
};

/**
 * Creates a new validation rule using the new ValidationRules endpoint
 *
 * @param validationRule - The validation rule to create
 * @returns Promise resolving to a ValidationRuleDto object
 */
export const createValidationRuleNew = async (
  validationRule: ValidationRuleDto
): Promise<ValidationRuleDto | null> => {
  try {
    const response = await axios.post(
      `${getServiceEndpoint()}/ValidationRules`,
      validationRule,
      buildClientHeader()
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to create validation rule", response.statusText);
      return null;
    }

    return response.data;
  } catch (error) {
    console.error("Error creating validation rule:", error);
    return null;
  }
};

/**
 * Retrieves supported rule types
 *
 * @returns Promise resolving to an array of supported rule type strings
 */
export const getSupportedRuleTypes = async (): Promise<string[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/ValidationRules/supported-rule-types`,
      buildClientHeader()
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve supported rule types", response.statusText);
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error("Error getting supported rule types:", error);
    return [];
  }
};

/**
 * Validates rules for a specific fund
 *
 * @param fundId - The ID of the fund to validate rules for
 * @returns Promise resolving to validation result (response data)
 */
export const validateRules = async (fundId: number): Promise<any> => {
  try {
    const baseHeaders = buildClientHeader();
    const headers = {
      ...baseHeaders,
      headers: {
        ...baseHeaders.headers,
        fundId: fundId.toString(),
      },
    };

    const response = await axios.get(
      `${getServiceEndpoint()}/ValidationRules/validate`,
      headers
    );

    if (response.status !== 200) {
      console.log("Failed to validate rules", response.statusText);
      return null;
    }

    return response.data;
  } catch (error) {
    console.error("Error validating rules:", error);
    return null;
  }
};


